import React, { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import Dropdown from "../../../components/ui/dropdown";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import Table from "../../../components/ui/table";
import Checkbox from "../../../components/ui/checkbox";
import styles from "./budget-lines.module.css";
import FormSection from "../../../components/screens/form-section";
import { getGuilds, getDefaultBudgets, getBudgetLinesSummary, getBudgetLineById, getRConjuntos, createBudgetLine, editBudgetLine } from "../../../services/apiService";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";
import apiClient from "../../../utils/api";
import usePageTitle from "../../../hooks/usePageTitle";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

const BudgetLines = () => {
  const { id: expedienteId } = useParams(); // Obtener el ID del expediente desde la URL
  usePageTitle(`Expediente ${expedienteId}`, 'Líneas Presupuesto');
  const { isLockedByOther } = useOutletContext();

  // Valores fijos de rentabilidad
  const RENTABILITY_MIN = 25;
  const RENTABILITY_MAX = 35;

  const [formData, setFormData] = useState({
    guild: null,
    defaultBudget: null,
    budgetNumber: "",
    // Costes fields
    workforce: "", // Mano de obra
    workerMaterial: "", // Material operario  
    companyMaterial: "", // Material empresa
    rebateWorkforce: "", // Rebajado mano de obra
    rebateWorkerMaterial: "", // Rebajado material operario
    quantity: "", // Cantidad
    // Valores presupuesto
    budgetPrice: "", // Precio presupuesto
    materialPrice: "", // Material (solo si desglosado)
    iva: null,
    totalCost: "",
    totalBudget: "",
    totalRConjunto: "",
    totalRentabilityPercentage: "", // Cambio de beneficio a rentabilidad
    totalRentability: "", // Cambio de beneficio a rentabilidad
    information: "",
    // Campos para los diferentes modos de valores
    rConjuntoMin: "",
    rConjuntoMax: "",
    priceMin: "",
    priceMax: "",
    rentabilityMin: "", // Cambio de beneficio a rentabilidad
    rentabilityMax: "", // Cambio de beneficio a rentabilidad
    workforcePriceMin: "", // Precio mano obra mínimo (modo desglosado)
    workforcePriceMax: "", // Precio mano obra máximo (modo desglosado)
    materialPriceMin: "", // Precio material mínimo (modo desglosado)
    materialPriceMax: "", // Precio material máximo (modo desglosado)
    materialUse: "", // Información de material
  });

  const [guildOptions, setGuildOptions] = useState([]);
  const [defaultBudgetOptions, setDefaultBudgetOptions] = useState([]);
  const [budgetLinesTableData, setBudgetLinesTableData] = useState([]);
  const [rConjuntosData, setRConjuntosData] = useState(null); // [adminCommission, rConjuntoMin, rConjuntoMax] - ej: [5.0, 0.65, 0.63]
  const [maxBudgetNumber, setMaxBudgetNumber] = useState(null);

  const [valuesMode, setValuesMode] = useState({
    value: "normal",
    label: "Normal",
  });

  const [costMode, setCostMode] = useState({
    value: "normal",
    label: "Normal",
  });
  const [isDone, setIsDone] = useState(false);
  const [doNotDo, setDoNotDo] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingBudgetLineId, setEditingBudgetLineId] = useState(null);

  // Estados para detectar cambios sin guardar
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    "Tienes cambios sin guardar en la línea de presupuesto."
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validar límite mínimo para budgetNumber
    if (name === "budgetNumber") {
      const numValue = parseInt(value);
      // No permitir valores menores a 1 (incluyendo 0)
      if (value !== "" && (!isNaN(numValue) && numValue < 1)) {
        return; // No actualizar si es menor que 1
      }
      // No permitir exceder el máximo
      if (maxBudgetNumber !== null && value !== "" && (!isNaN(numValue) && numValue > maxBudgetNumber)) {
        return; // No actualizar si excede el máximo
      }
    }
    
    setFormData({ ...formData, [name]: value });
  };

  // Función para resetear el formulario y modo de edición
  const resetForm = () => {
    // Obtener rConjuntos desde la API si están disponibles
    let rConjuntoMinValue = "";
    let rConjuntoMaxValue = "";
    if (rConjuntosData && rConjuntosData.length >= 3) {
      const [, rConjuntoMin, rConjuntoMax] = rConjuntosData;
      rConjuntoMinValue = rConjuntoMin.toFixed(3);
      rConjuntoMaxValue = rConjuntoMax.toFixed(3);
    }

    setFormData({
      guild: null,
      defaultBudget: null,
      budgetNumber: maxBudgetNumber ? Math.max(1, maxBudgetNumber - 1).toString() : "1",
      // Costes fields
      workforce: "",
      workerMaterial: "",
      companyMaterial: "",
      rebateWorkforce: "",
      rebateWorkerMaterial: "",
      quantity: "",
      // Valores presupuesto
      budgetPrice: "",
      materialPrice: "",
      iva: null,
      totalCost: "",
      totalBudget: "",
      totalRConjunto: "",
      totalRentabilityPercentage: "",
      totalRentability: "",
      information: "",
      // Campos para rangos - mantener valores de rConjuntos desde la API
      rConjuntoMin: rConjuntoMinValue,
      rConjuntoMax: rConjuntoMaxValue,
      priceMin: "",
      priceMax: "",
      rentabilityMin: "",
      rentabilityMax: "",
      workforcePriceMin: "",
      workforcePriceMax: "",
      materialPriceMin: "",
      materialPriceMax: "",
      materialUse: "",
    });
    setValuesMode({ value: "normal", label: "Normal" });
    setCostMode({ value: "normal", label: "Normal" });
    setIsDone(false);
    setDoNotDo(false);
    setIsEditMode(false);
    setEditingBudgetLineId(null);
  };

  // Función para calcular la suma de costes (mano de obra + material operario + material empresa)
  const calculateTotalCosts = (workforce, workerMaterial, companyMaterial, quantity = 1) => {
    const workforceVal = parseFloat(workforce) || 0;
    const workerMaterialVal = parseFloat(workerMaterial) || 0;
    const companyMaterialVal = parseFloat(companyMaterial) || 0;
    const quantityVal = parseFloat(quantity) || 1;
    
    return (workforceVal + workerMaterialVal + companyMaterialVal) * quantityVal;
  };

  // Función para calcular precios usando rConjuntos (modo normal y desglosado)
  const calculatePricesFromRConjunto = (totalCosts, rConjuntoMin, rConjuntoMax) => {
    if (!totalCosts || !rConjuntoMin || !rConjuntoMax) return { priceMin: 0, priceMax: 0 };
    
    // Precio mínimo = suma de costes / rConjunto mínimo (el mayor valor 0.65)
    // Precio máximo = suma de costes / rConjunto máximo (el menor valor 0.63)
    // Al dividir por un número mayor, se obtiene un resultado menor (por eso priceMin usa rConjuntoMin)
    const priceMin = totalCosts / rConjuntoMin;
    const priceMax = totalCosts / rConjuntoMax;

    return { priceMin, priceMax };
  };

  // Función para calcular precios en modo beneficio/rentabilidad
  const calculatePricesFromRentability = (totalCosts, adminCommission = 10) => {
    if (!totalCosts) return { priceMin: 0, priceMax: 0 };
    
    const X = totalCosts;
    const Y_min = RENTABILITY_MIN; // Porcentaje rentabilidad mínimo
    const Y_max = RENTABILITY_MAX; // Porcentaje rentabilidad máximo
    const Z = adminCommission; // Comisión del administrador
    
    // precio min = (X * (1 + Y / 100)) / (1 - Z / 100)
    const priceMin = (X * (1 + Y_min / 100)) / (1 - Z / 100);
    const priceMax = (X * (1 + Y_max / 100)) / (1 - Z / 100);
    
    return { priceMin, priceMax };
  };

  // Función para calcular el porcentaje de rentabilidad
  const calculateRentabilityPercentage = (budgetPrice, totalCosts, adminCommission = 10) => {
    if (!budgetPrice || !totalCosts || budgetPrice === 0 || totalCosts === 0) return 0;
    
    const P = parseFloat(budgetPrice);
    const X = parseFloat(totalCosts);
    const Z = adminCommission;
    
    // porcentaje rentabilidad = 100 * ((P * (1 - Z / 100)) / X - 1)
    const rentabilityPercentage = 100 * ((P * (1 - Z / 100)) / X - 1);
    return rentabilityPercentage;
  };

  // Función para calcular la rentabilidad en euros
  const calculateRentability = (rentabilityPercentage, totalCosts) => {
    if (!rentabilityPercentage || !totalCosts) return 0;
    
    const rentability = (parseFloat(rentabilityPercentage) * parseFloat(totalCosts)) / 100;
    return rentability;
  };

  // Función para calcular rConjunto
  const calculateRConjunto = (totalCosts, budgetPrice) => {
    if (!totalCosts || !budgetPrice || budgetPrice === 0) return 0;
    
    return totalCosts / budgetPrice;
  };

  // Efecto para calcular automáticamente cuando cambian los valores
  useEffect(() => {
    const workforceVal = parseFloat(formData.workforce) || 0;
    const workerMaterialVal = parseFloat(formData.workerMaterial) || 0;
    const companyMaterialVal = parseFloat(formData.companyMaterial) || 0;
    const quantityVal = parseFloat(formData.quantity) || 1;
    const budgetPriceVal = parseFloat(formData.budgetPrice) || 0;
    const materialPriceVal = parseFloat(formData.materialPrice) || 0;
    
    // Calcular costes totales
    const totalCosts = calculateTotalCosts(formData.workforce, formData.workerMaterial, formData.companyMaterial, formData.quantity);
    
    // Calcular presupuesto total
    let totalBudget = budgetPriceVal;
    if (valuesMode.value === "detailed" && materialPriceVal) {
      totalBudget += materialPriceVal;
    }
    
    // Calcular rConjunto
    const rConjunto = calculateRConjunto(totalCosts, totalBudget);
    
    // Calcular porcentaje de rentabilidad (necesita comisión del administrador de rConjuntosData)
    const adminCommission = rConjuntosData ? rConjuntosData[0] : 10;
    const rentabilityPercentage = calculateRentabilityPercentage(totalBudget, totalCosts, adminCommission);
    
    // Calcular rentabilidad en euros
    const rentability = calculateRentability(rentabilityPercentage, totalCosts);
    
    // Calcular precios según el modo de valores
    let priceMin = 0, priceMax = 0;
    let workforcePriceMin = 0, workforcePriceMax = 0;
    let materialPriceMin = 0, materialPriceMax = 0;
    
    if (rConjuntosData && totalCosts > 0) {
      // const [, rConjuntoMin, rConjuntoMax] = rConjuntosData; // Unused for now
      
      if (valuesMode.value === "normal" && formData.rConjuntoMin && formData.rConjuntoMax) {
        const prices = calculatePricesFromRConjunto(totalCosts, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        priceMin = prices.priceMin;
        priceMax = prices.priceMax;
      } else if (valuesMode.value === "benefit") {
        const prices = calculatePricesFromRentability(totalCosts, adminCommission);
        priceMin = prices.priceMin;
        priceMax = prices.priceMax;
      } else if (valuesMode.value === "detailed" && formData.rConjuntoMin && formData.rConjuntoMax) {
        // Para desglosado calculamos 4 veces
        const workforceCost = workforceVal * quantityVal;
        const materialCost = (workerMaterialVal + companyMaterialVal) * quantityVal;
        
        const workforcePrices = calculatePricesFromRConjunto(workforceCost, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        const materialPrices = calculatePricesFromRConjunto(materialCost, parseFloat(formData.rConjuntoMin), parseFloat(formData.rConjuntoMax));
        
        workforcePriceMin = workforcePrices.priceMin;
        workforcePriceMax = workforcePrices.priceMax;
        materialPriceMin = materialPrices.priceMin;
        materialPriceMax = materialPrices.priceMax;
      }
    }
    
    // Actualizar campos calculados en totales y valores
    setFormData(prev => ({
      ...prev,
      totalCost: totalCosts.toFixed(2),
      totalBudget: totalBudget.toFixed(2),
      totalRConjunto: rConjunto.toFixed(4),
      totalRentabilityPercentage: rentabilityPercentage.toFixed(2),
      totalRentability: rentability.toFixed(2),
      priceMin: priceMin.toFixed(2),
      priceMax: priceMax.toFixed(2),
      workforcePriceMin: workforcePriceMin.toFixed(2),
      workforcePriceMax: workforcePriceMax.toFixed(2),
      materialPriceMin: materialPriceMin.toFixed(2),
      materialPriceMax: materialPriceMax.toFixed(2),
    }));
    
  }, [
    formData.workforce, 
    formData.workerMaterial, 
    formData.companyMaterial, 
    formData.quantity,
    formData.budgetPrice, 
    formData.materialPrice,
    formData.rConjuntoMin,
    formData.rConjuntoMax,
    valuesMode.value,
    rConjuntosData
  ]);

  // useEffect para establecer valores fijos de R. conjunto desde la API
  useEffect(() => {
    if (rConjuntosData && rConjuntosData.length >= 3) {
      // La API devuelve [adminCommission, valor1, valor2]
      // donde valor1 > valor2, por lo que valor1 es el MÍNIMO y valor2 es el MÁXIMO
      const [, rConjuntoMin, rConjuntoMax] = rConjuntosData;
      console.log("R. Conjunto Min:", rConjuntoMin);
      console.log("R. Conjunto Max:", rConjuntoMax);
      setFormData(prev => ({
        ...prev,
        rConjuntoMin: rConjuntoMin.toFixed(3), // Valor fijo desde la API
        rConjuntoMax: rConjuntoMax.toFixed(3), // Valor fijo desde la API
      }));
    }
  }, [rConjuntosData]);

  // Función para invalidar y recargar los datos (simula el comportamiento de tags)
  const invalidateBudgetData = async () => {
    try {
      // Invalidar líneas de presupuesto
      const budgetLines = await getBudgetLinesSummary(expedienteId);
      const mappedBudgetLines = budgetLines.map(item => ({
        id: item.id,
        guild: item.gremio,
        budgetNumber: item.numPresupuesto,
        budget: item.presupuestoTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }),
        cost: item.costeTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }),
        rebate: item.conjuntoTotal?.toFixed(4) || "0.00",
        profit: item.beneficioTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }),
        done: item.realizado ? "Realizado" : "Pendiente",
        disabled: item.noRealizar ? "No realizar" : "-- --",
      }));
      setBudgetLinesTableData(mappedBudgetLines);
      
      // Invalidar número de presupuesto
      const lastBudgetResponse = await apiClient.get(`/presupuesto/ultimoNumPresupuesto?expedienteId=${expedienteId}`);
      const lastBudgetNumber = lastBudgetResponse.data;
      const currentBudgetNumber = Math.max(1, lastBudgetNumber); // Mínimo 1
      const maxAllowed = lastBudgetNumber + 1;
      setMaxBudgetNumber(maxAllowed);
      
      // Solo actualizar el número si no estamos en modo edición
      if (!isEditMode) {
        setFormData(prev => ({
          ...prev,
          budgetNumber: currentBudgetNumber.toString()
        }));
      }
    } catch (error) {
      console.error("Error invalidating budget data:", error);
    }
  };

  const handleDropdownSelect = async (field, option) => {
    if (field === "defaultBudget" && option) {
      // Cuando se selecciona un presupuesto predeterminado, rellenar la descripción
      try {
        const response = await apiClient.get('/ptoPred/all');
        const predeterminedBudgets = response.data;
        const selectedBudget = predeterminedBudgets.find(budget => budget.id === option.value);
        
        if (selectedBudget) {
          setFormData({ 
            ...formData, 
            [field]: option,
            information: selectedBudget.descripcion || ""
          });
        } else {
          setFormData({ ...formData, [field]: option });
        }
      } catch (error) {
        console.error("Error fetching predetermined budget description:", error);
        setFormData({ ...formData, [field]: option });
      }
    } else {
      setFormData({ ...formData, [field]: option });
    }
  };

  const handleCostModeChange = (option) => {
    setCostMode(option);
    // Limpiar campos según el modo seleccionado
    if (option.value === "normal") {
      setFormData({
        ...formData,
        rebateWorkforce: "",
        rebateWorkerMaterial: "",
        quantity: "",
      });
    } else if (option.value === "rebate") {
      setFormData({
        ...formData,
        quantity: "",
      });
    } else if (option.value === "quantity") {
      setFormData({
        ...formData,
        rebateWorkforce: "",
        rebateWorkerMaterial: "",
      });
    }
  };

  const handleIsDoneChange = (checked) => {
    setIsDone(checked);
    if (checked) setDoNotDo(false);
  };

  const handleDoNotDoChange = (checked) => {
    setDoNotDo(checked);
    if (checked) setIsDone(false);
  };

  // useEffect para detectar cambios sin guardar
  useEffect(() => {
    if (!initialFormData) {
      setHasUnsavedChanges(false);
      return;
    }

    // Comparar solo los campos que el usuario puede modificar directamente (excluir campos calculados)
    const userEditableFields = [
      'guild', 'defaultBudget', 'budgetNumber', 
      'workforce', 'workerMaterial', 'companyMaterial',
      'rebateWorkforce', 'rebateWorkerMaterial', 'quantity',
      'budgetPrice', 'materialPrice', 'information', 'materialUse'
    ];
    
    const currentValues = {};
    const initialValues = {};
    
    userEditableFields.forEach(field => {
      currentValues[field] = JSON.stringify(formData[field]);
      initialValues[field] = JSON.stringify(initialFormData[field]);
    });
    
    const formChanged = JSON.stringify(currentValues) !== JSON.stringify(initialValues);
    const checkboxesChanged = (isDone !== initialFormData.isDone) || (doNotDo !== initialFormData.doNotDo);
    const modesChanged = (JSON.stringify(valuesMode) !== JSON.stringify(initialFormData.valuesMode)) || 
                        (JSON.stringify(costMode) !== JSON.stringify(initialFormData.costMode));

    setHasUnsavedChanges(formChanged || checkboxesChanged || modesChanged);
  }, [formData, isDone, doNotDo, valuesMode, costMode, initialFormData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.guild) {
      showErrorToast("El campo Gremio es obligatorio");
      return;
    }
    if (!formData.budgetNumber) {
      showErrorToast("El campo Nº de presupuesto es obligatorio");
      return;
    }
    if (!formData.workforce || parseFloat(formData.workforce) === 0) {
      showErrorToast("El campo Mano de obra es obligatorio");
      return;
    }
    if (!formData.budgetPrice || parseFloat(formData.budgetPrice) === 0) {
      showErrorToast("El campo Precio presupuesto es obligatorio");
      return;
    }
    
    try {
      // Mapear los datos del formulario al formato esperado por la API
      const budgetLineData = {
        gremioId: formData.guild?.value || null,
        ptoPredId: formData.defaultBudget?.value || null,
        expedienteId: parseInt(expedienteId.toString().replace(/\s+/g, '')),
        numPresupuesto: parseInt(formData.budgetNumber) || null,
        realizado: isDone,
        noRealizar: doNotDo,
        
        // Costes
        costeManoObraOperario: parseFloat(formData.workforce) || 0.0,
        costeMaterialOperario: parseFloat(formData.workerMaterial) || 0.0,
        costeMaterialEmpresa: parseFloat(formData.companyMaterial) || 0.0,
        tipoCoste: costMode?.label || "Normal",
        costeManoObraOperarioRebajado: parseFloat(formData.rebateWorkforce) || 0.0,
        costeMaterialOperarioRebajado: parseFloat(formData.rebateWorkerMaterial) || 0.0,
        cantidad: parseFloat(formData.quantity) || 1,
        
        // Presupuesto
        presupuestoManoObra: parseFloat(formData.budgetPrice) || 0.0,
        presupuestoMaterial: parseFloat(formData.materialPrice) || 0.0,
        tipoValores: valuesMode?.label || "Normal",
        
        // Totales
        costeTotal: parseFloat(formData.totalCost) || 0.0,
        presupuestoTotal: parseFloat(formData.totalBudget) || 0.0,
        conjuntoTotal: parseFloat(formData.totalRConjunto) || 0.0,
        porcentageBeneficioTotal: parseFloat(formData.totalRentabilityPercentage) || 0.0,
        beneficioTotal: parseFloat(formData.totalRentability) || 0.0,
        
        // Información adicional
        infoTrabajo: formData.information || "",
        infoMaterial: formData.materialUse || ""
      };

      console.log("Sending budget line data:", budgetLineData);
      
      let result;
      if (isEditMode && editingBudgetLineId) {
        // Modo edición: usar editBudgetLine
        result = await editBudgetLine(editingBudgetLineId, budgetLineData);
        console.log("Budget line edited successfully:", result);
        showSuccessToast(notifications.update.success("Línea de presupuesto"));
      } else {
        // Modo crear: usar createBudgetLine
        result = await createBudgetLine(budgetLineData);
        console.log("Budget line created successfully:", result);
        showSuccessToast(notifications.create.success("Línea de presupuesto"));
      }
      
      // Resetear el formulario después del éxito y estado de cambios
      setInitialFormData(null);
      setHasUnsavedChanges(false);
      resetForm();
      
      // Invalidar y recargar los datos (simula tags de React Query)
      await invalidateBudgetData();
      
    } catch (error) {
      console.error("Error creating budget line:", error);
      showErrorToast(isEditMode ? notifications.update.error("la línea de presupuesto") : notifications.create.error("la línea de presupuesto"));
    }
  };

  const handleBudgetLineClick = async (budgetLine) => {
    console.log("Budget line clicked:", budgetLine);
    
    if (!budgetLine.id) {
      console.error("No ID found in budget line");
      return;
    }

    try {
      // Obtener detalles completos de la línea de presupuesto
      const details = await getBudgetLineById(budgetLine.id);
      console.log("Budget line details:", details);

      // Buscar el gremio correspondiente en las opciones
      const selectedGuild = guildOptions.find(guild => guild.value === details.gremioId);
      
      // Buscar el presupuesto por defecto correspondiente
      const selectedDefaultBudget = defaultBudgetOptions.find(budget => budget.value === details.ptoPredId);

      // Mapear tipo de coste
      const costModeMap = {
        "Normal": { value: "normal", label: "Normal" },
        "Rebajado": { value: "rebate", label: "Rebajado" },
        "Cantidad": { value: "quantity", label: "Cantidad" }
      };
      const selectedCostMode = costModeMap[details.tipoCoste] || { value: "normal", label: "Normal" };

      // Mapear tipo de valores
      const valuesModeMap = {
        "Normal": { value: "normal", label: "Normal" },
        "Desglosado": { value: "detailed", label: "Desglosado" },
        "Rentabilidad": { value: "benefit", label: "Rentabilidad" }
      };
      const selectedValuesMode = valuesModeMap[details.tipoValores] || { value: "normal", label: "Normal" };

      // Obtener valores de rConjuntos desde rConjuntosData si no vienen en la línea de presupuesto
      let rConjuntoMinValue = details.rconjuntoMinimo?.toString() || "";
      let rConjuntoMaxValue = details.rconjuntoMaximo?.toString() || "";
      
      // Si no hay valores en la línea, usar los de rConjuntosData
      if ((!rConjuntoMinValue || !rConjuntoMaxValue) && rConjuntosData && rConjuntosData.length >= 3) {
        const [, rConjuntoMin, rConjuntoMax] = rConjuntosData;
        if (!rConjuntoMinValue) rConjuntoMinValue = rConjuntoMin.toFixed(3);
        if (!rConjuntoMaxValue) rConjuntoMaxValue = rConjuntoMax.toFixed(3);
      }

      // Rellenar el formulario con todos los datos de la API
      // IMPORTANTE: Primero actualizamos los modos para que el useEffect calcule correctamente
      setCostMode(selectedCostMode);
      setValuesMode(selectedValuesMode);
      
      // Luego establecemos solo los campos base, NO los calculados
      // El useEffect se encargará de calcular priceMin, priceMax, totales, etc.
      setFormData(prev => ({
        ...prev,
        guild: selectedGuild || null,
        defaultBudget: selectedDefaultBudget || null,
        budgetNumber: details.numPresupuesto?.toString() || "",
        // Costes fields - campos base que vienen de la API
        workforce: details.costeManoObraOperario?.toString() || "",
        workerMaterial: details.costeMaterialOperario?.toString() || "",
        companyMaterial: details.costeMaterialEmpresa?.toString() || "",
        rebateWorkforce: details.costeManoObraOperarioRebajado?.toString() || "",
        rebateWorkerMaterial: details.costeMaterialOperarioRebajado?.toString() || "",
        quantity: details.cantidad?.toString() || "",
        // Valores presupuesto - campos base que vienen de la API
        budgetPrice: details.presupuestoManoObra?.toString() || "",
        materialPrice: details.presupuestoMaterial?.toString() || "",
        iva: null,
        information: details.infoTrabajo || "",
        materialUse: details.infoMaterial || "",
        // Campos para rangos - mantener valores de rConjuntos
        rConjuntoMin: rConjuntoMinValue,
        rConjuntoMax: rConjuntoMaxValue,
        // NO establecer los campos calculados aquí - el useEffect los calculará
        // Esto evita que se sobrescriban con valores vacíos de la API
      }));
      
      // Actualizar checkboxes
      setIsDone(details.realizado || false);
      setDoNotDo(details.noRealizar || false);

      // Activar modo de edición
      setIsEditMode(true);
      setEditingBudgetLineId(budgetLine.id);

      // Guardar estado inicial para detectar cambios
      setInitialFormData({
        guild: selectedGuild || null,
        defaultBudget: selectedDefaultBudget || null,
        budgetNumber: details.numPresupuesto?.toString() || "",
        workforce: details.costeManoObraOperario?.toString() || "",
        workerMaterial: details.costeMaterialOperario?.toString() || "",
        companyMaterial: details.costeMaterialEmpresa?.toString() || "",
        rebateWorkforce: details.costeManoObraOperarioRebajado?.toString() || "",
        rebateWorkerMaterial: details.costeMaterialOperarioRebajado?.toString() || "",
        quantity: details.cantidad?.toString() || "",
        budgetPrice: details.presupuestoManoObra?.toString() || "",
        materialPrice: details.presupuestoMaterial?.toString() || "",
        iva: null,
        information: details.infoTrabajo || "",
        materialUse: details.infoMaterial || "",
        rConjuntoMin: rConjuntoMinValue,
        rConjuntoMax: rConjuntoMaxValue,
        isDone: details.realizado || false,
        doNotDo: details.noRealizar || false,
        valuesMode: selectedValuesMode,
        costMode: selectedCostMode,
      });

    } catch (error) {
      console.error("Error fetching budget line details:", error);
      showErrorToast("Error al cargar los detalles de la línea de presupuesto");
    }
  };

  const costModeOptions = [
    { value: "normal", label: "Normal" },
    { value: "rebate", label: "Rebajado" },
    { value: "quantity", label: "Cantidad" },
  ];

  // Datos para la tabla de líneas de presupuesto
  const budgetLinesTableHeaders = [
    { label: "Gremio", key: "guild" },
    { label: "Nº Presupuesto", key: "budgetNumber" },
    { label: "Presupuesto", key: "budget" },
    { label: "Coste", key: "cost" },
    { label: "R. Conjunto", key: "rebate" },
    { label: "Beneficio", key: "profit" },
    { label: "Realizado", key: "done" },
    { label: "No realizar", key: "disabled" },
  ];

  const valuesModeOptions = [
    { value: "normal", label: "Normal" },
    { value: "benefit", label: "Rentabilidad" },
    { value: "detailed", label: "Desglosado" },
  ];

  const textColorConditions = {
    done: [
      {
        test: (value) => value === "Pendiente",
        color: "var(--progress)",
      },
      {
        test: (value) => value === "Realizado",
        color: "var(--green)",
      },
    ],
    disabled: [
      {
        test: (value) => !!value,
        color: "var(--red)",
      },
    ],
  };

  const handleValuesModeChange = (option) => {
    setValuesMode(option);
    // Limpiar campos específicos al cambiar de modo (preservando rConjunto que vienen de API)
    switch (option.value) {
      case "normal":
        setFormData(prev => ({
          ...prev,
          rentabilityMin: "",
          rentabilityMax: "",
          workforcePriceMin: "",
          workforcePriceMax: "",
          materialPriceMin: "",
          materialPriceMax: "",
          // Preservar rConjuntoMin y rConjuntoMax (vienen de API)
        }));
        break;
      case "benefit":
        setFormData(prev => ({
          ...prev,
          // NO limpiar rConjuntoMin y rConjuntoMax porque vienen de la API
          workforcePriceMin: "",
          workforcePriceMax: "",
          materialPriceMin: "",
          materialPriceMax: "",
        }));
        break;
      case "detailed":
        setFormData(prev => ({
          ...prev,
          // NO limpiar rConjuntoMin y rConjuntoMax porque vienen de la API
          rentabilityMin: "",
          rentabilityMax: "",
        }));
        break;
      default:
        // No hacer nada para otros casos
        break;
    }
  };

  const renderValuesFields = () => {
    switch (valuesMode.value) {
      case "normal":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.specialRow}>
              <Input
                label="rConjunto mínimo"
                name="rConjuntoMin"
                placeholder={"rConjunto"}
                value={formData.rConjuntoMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="rConjunto máximo"
                name="rConjuntoMax"
                placeholder={"rConjunto"}
                value={formData.rConjuntoMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
            <div className={styles.specialRow}>
              <Input
                label="Precio mínimo"
                name="priceMin"
                placeholder={"Precio"}
                value={formData.priceMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="Precio máximo"
                name="priceMax"
                placeholder={"Precio"}
                value={formData.priceMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
          </div>
        );
      case "benefit":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.specialRow}>
              <Input
                label="Rentabilidad mínima (%)"
                name="rentabilityMin"
                placeholder={RENTABILITY_MIN.toString()}
                value={RENTABILITY_MIN.toString()}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="Rentabilidad máxima (%)"
                name="rentabilityMax"
                placeholder={RENTABILITY_MAX.toString()}
                value={RENTABILITY_MAX.toString()}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
            <div className={styles.specialRow}>
              <Input
                label="Precio mínimo"
                name="priceMin"
                placeholder={"Precio"}
                value={formData.priceMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="Precio máximo"
                name="priceMax"
                placeholder={"Precio"}
                value={formData.priceMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
          </div>
        );
      case "detailed":
        return (
          <div className={styles.valuesContainer}>
            <div className={styles.specialRow}>
              <Input
                label="rConjunto mínimo"
                name="rConjuntoMin"
                placeholder={"rConjunto"}
                value={formData.rConjuntoMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="rConjunto máximo"
                name="rConjuntoMax"
                placeholder={"rConjunto"}
                value={formData.rConjuntoMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
            <div className={styles.specialRow}>
              <Input
                label="Precio mano obra mín"
                name="workforcePriceMin"
                placeholder={"Precio"}
                value={formData.workforcePriceMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="Precio mano obra máx"
                name="workforcePriceMax"
                placeholder={"Precio"}
                value={formData.workforcePriceMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
            <div className={styles.specialRow}>
              <Input
                label="Precio material mín"
                name="materialPriceMin"
                placeholder={"Precio"}
                value={formData.materialPriceMin}
                style={{ flex: 1 }}
                readOnly
              />
              <Input
                label="Precio material máx"
                name="materialPriceMax"
                placeholder={"Precio"}
                value={formData.materialPriceMax}
                style={{ flex: 1 }}
                readOnly
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Verificar que tenemos un expedienteId válido
        if (!expedienteId) {
          console.error("No se encontró expedienteId en la URL");
          return;
        }

        const guilds = await getGuilds();
        setGuildOptions(
          guilds.map((item) => ({ value: item.id, label: item.nombre }))
        );

        const defaultBudgets = await getDefaultBudgets();
        setDefaultBudgetOptions(
          defaultBudgets.map((item) => ({ value: item.id, label: item.nombre }))
        );

        // Cargar rConjuntos con el expedienteId dinámico
        const rConjuntos = await getRConjuntos(parseInt(expedienteId.toString().replace(/\s+/g, '')));
        setRConjuntosData(rConjuntos); // [adminCommission, rConjuntoMin, rConjuntoMax] - ej: [5.0, 0.65, 0.63]

        // Cargar líneas de presupuesto con el expedienteId dinámico
        const budgetLines = await getBudgetLinesSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
        const mappedBudgetLines = budgetLines.map(item => ({
          id: item.id, // Agregar el ID para el click
          guild: item.gremio,
          budgetNumber: item.numPresupuesto,
          budget: item.presupuestoTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }),
          cost: item.costeTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }),
          rebate: item.conjuntoTotal?.toFixed(4) || "0.00",
          profit: item.beneficioTotal?.toLocaleString("es-ES", { style: "currency", currency: "EUR" }), // Cambiado de rentabilidadTotal a beneficioTotal
          done: item.realizado ? "Realizado" : "Pendiente",
          disabled: item.noRealizar ? "No realizar" : "-- --",
        }));
        setBudgetLinesTableData(mappedBudgetLines);

        // Cargar el último número de presupuesto
        const lastBudgetResponse = await apiClient.get(`/presupuesto/ultimoNumPresupuesto?expedienteId=${parseInt(expedienteId.toString().replace(/\s+/g, ''))}`);
        const lastBudgetNumber = lastBudgetResponse.data;
        const currentBudgetNumber = Math.max(1, lastBudgetNumber); // Mínimo 1
        const maxAllowed = lastBudgetNumber + 1;
        setMaxBudgetNumber(maxAllowed);
        
        // Establecer automáticamente el número de presupuesto
        setFormData(prev => ({
          ...prev,
          budgetNumber: currentBudgetNumber.toString()
        }));
        
        // Guardar estado inicial para modo creación después de cargar rConjuntos
        // Esto permite detectar cambios cuando el usuario empieza a llenar el formulario
        setTimeout(() => {
          setInitialFormData({
            guild: null,
            defaultBudget: null,
            budgetNumber: currentBudgetNumber.toString(),
            workforce: "",
            workerMaterial: "",
            companyMaterial: "",
            rebateWorkforce: "",
            rebateWorkerMaterial: "",
            quantity: "",
            budgetPrice: "",
            materialPrice: "",
            iva: null,
            information: "",
            materialUse: "",
            isDone: false,
            doNotDo: false,
            valuesMode: { value: "normal", label: "Normal" },
            costMode: { value: "normal", label: "Normal" },
          });
        }, 500); // Esperar a que se apliquen los valores de rConjuntos
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [expedienteId]); // Agregar expedienteId como dependencia

  return (
    <div style={{ paddingTop: "24px" }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "24px" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "12px",
          }}
        >
          <div>
            <FormSection
              title="General"
              style={{ marginBottom: "12px" }}
              renderRight={() => (
                <div className={styles.checkboxContainer}>
                  <Checkbox
                    label="¿Realizado?"
                    checked={isDone}
                    onChange={handleIsDoneChange}
                  />
                  <Checkbox
                    label="No realizar"
                    checked={doNotDo}
                    onChange={handleDoNotDoChange}
                  />
                </div>
              )}
            >
              <div className={styles.sectionRow}>
                <Dropdown
                  label={"Gremio"}
                  options={guildOptions}
                  onSelect={(option) => handleDropdownSelect("guild", option)}
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.guild}
                />

                <Dropdown
                  label={"Pto. Predeterminado"}
                  options={defaultBudgetOptions}
                  onSelect={(option) =>
                    handleDropdownSelect("defaultBudget", option)
                  }
                  style={{ marginTop: "3px", width: "100%" }}
                  mainStyle={{ flex: 1 }}
                  selected={formData.defaultBudget}
                  clearable
                />

                <div style={{ flex: 1 }}>
                  <Input
                    label="Nº de presupuesto"
                    name="budgetNumber"
                    placeholder={"Numero"}
                    value={formData.budgetNumber}
                    onChange={handleInputChange}
                    type="number"
                    min="1"
                    max={maxBudgetNumber || undefined}
                    style={{ flex: 1 }}
                  />
                  {maxBudgetNumber && (
                    <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                      Máximo permitido: {maxBudgetNumber}
                    </small>
                  )}
                </div>
              </div>
            </FormSection>
            <div className={styles.sectionRow}>
              <div className={styles.leftColumn}>
                <FormSection
                  title="Costes"
                  renderRight={() => (
                    <div className={styles.checkboxContainer}>
                      <Dropdown
                        options={costModeOptions}
                        onSelect={handleCostModeChange}
                        style={{ height: "25px" }}
                        mainStyle={{ flex: 1 }}
                        selected={costMode}
                        clearable={false}
                      />
                    </div>
                  )}
                >
                  <div className={styles.inputRow}>
                    <Input
                      label="Mano de obra"
                      name="workforce"
                      placeholder={"Coste"}
                      value={formData.workforce}
                      onChange={handleInputChange}
                      style={{ flex: 1 }}
                    />

                    <Input
                      label="Material operario"
                      name="workerMaterial"
                      placeholder={"Coste"}
                      value={formData.workerMaterial}
                      onChange={handleInputChange}
                      style={{ flex: 1 }}
                    />

                    <Input
                      label="Material empresa"
                      name="companyMaterial"
                      placeholder={"Coste"}
                      value={formData.companyMaterial}
                      onChange={handleInputChange}
                      style={{ flex: 1 }}
                    />
                  </div>

                  {costMode.value === "rebate" && (
                    <div className={styles.inputRow}>
                      <Input
                        label="Rebajado mano obra"
                        name="rebateWorkforce"
                        placeholder={"Rebaja"}
                        value={formData.rebateWorkforce}
                        onChange={handleInputChange}
                        style={{ flex: 1 }}
                      />
                      <Input
                        label="Rebajado material operario"
                        name="rebateWorkerMaterial"
                        placeholder={"Rebaja"}
                        value={formData.rebateWorkerMaterial}
                        onChange={handleInputChange}
                        style={{ flex: 1 }}
                      />
                    </div>
                  )}

                  {costMode.value === "quantity" && (
                    <div className={styles.inputRow}>
                      <Input
                        label="Cantidad"
                        name="quantity"
                        placeholder={"Cantidad"}
                        value={formData.quantity}
                        onChange={handleInputChange}
                        style={{ flex: 1 }}
                      />
                    </div>
                  )}
                </FormSection>
                <FormSection title="Valores presupuesto">
                  <div className={styles.inputRow}>
                    <Input
                      label="Precio presupuesto"
                      name="budgetPrice"
                      placeholder={"Precio"}
                      value={formData.budgetPrice}
                      onChange={handleInputChange}
                      style={{ flex: 1 }}
                    />

                    {valuesMode.value === "detailed" && (
                      <Input
                        label="Material"
                        name="materialPrice"
                        placeholder={"Precio"}
                        value={formData.materialPrice}
                        onChange={handleInputChange}
                        style={{ flex: 1 }}
                      />
                    )}
                  </div>
                </FormSection>
              </div>

              <FormSection
                title="Valores"
                className={styles.rightColumn}
                renderRight={() => (
                  <div className={styles.checkboxContainer}>
                    <Dropdown
                      options={valuesModeOptions}
                      onSelect={handleValuesModeChange}
                      style={{ height: "25px" }}
                      mainStyle={{ flex: 1 }}
                      selected={valuesMode}
                      clearable={false}
                    />
                  </div>
                )}
              >
                {renderValuesFields()}
              </FormSection>
            </div>

            <FormSection title="Totales">
              <div className={styles.sectionRow}>
                <Input
                  label="Coste"
                  name="totalCost"
                  placeholder={"Precio"}
                  value={formData.totalCost}
                  style={{ flex: 1 }}
                  readOnly
                />

                <Input
                  label="Presupuesto"
                  name="totalBudget"
                  placeholder={"Precio"}
                  value={formData.totalBudget}
                  style={{ flex: 1 }}
                  readOnly
                />

                <Input
                  label="R. conjunto"
                  name="totalRConjunto"
                  placeholder={"rConjunto"}
                  value={formData.totalRConjunto}
                  style={{ flex: 1 }}
                  readOnly
                />
              </div>
              <div className={styles.sectionRow}>
                <Input
                  label="Porcentaje de rentabilidad"
                  name="totalRentabilityPercentage"
                  placeholder={"Porcentaje"}
                  value={formData.totalRentabilityPercentage ? `${formData.totalRentabilityPercentage} %` : ""}
                  style={{ flex: 1 }}
                  readOnly
                />

                <Input
                  label="Rentabilidad"
                  name="totalRentability"
                  placeholder={"Euros"}
                  value={formData.totalRentability ? `${formData.totalRentability} €` : ""}
                  style={{ flex: 1 }}
                  readOnly
                />
              </div>
            </FormSection>
          </div>
          <div className={styles.workMaterialContainer}>
            <FormSection
              title="Trabajos a realizar"
              style={{
                flex: 1,
                display: "flex",
                minWidth: "400px",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <textarea
                name="information"
                placeholder="Trabajos que se van a realizar"
                value={formData.information}
                onChange={handleInputChange}
                className={styles.workTextarea}
                style={{
                  width: "100%",
                  height: valuesMode?.value === "detailed" ? "444px" : "502px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontFamily: "var(--font-roboto)",
                  fontSize: "14px",
                  color: "black",
                  resize: "none",
                  outline: "none",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  overflow: "auto",
                }}
                spellCheck="true"
              />
            </FormSection>
            {valuesMode && valuesMode.value === "detailed" && (
              <FormSection
                title="Material"
                style={{
                  flex: 1,
                  display: "flex",
                  minWidth: "400px",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <textarea
                  name="materialUse"
                  placeholder="Materiales que se usarán"
                  value={formData.materialUse}
                  onChange={handleInputChange}
                  className={styles.workTextarea}
                  style={{
                    width: "100%",
                    height: "80px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontFamily: "var(--font-roboto)",
                    fontSize: "14px",
                    color: "black",
                    resize: "none",
                    outline: "none",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    overflow: "auto",
                  }}
                  spellCheck="true"
                />
              </FormSection>
            )}
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button type="submit" disabled={isLockedByOther}>
            {isEditMode ? "GUARDAR" : "CREAR LÍNEA"}
          </Button>
          {isEditMode && (
            <Button type="button" variant="secondary" onClick={() => {
              setInitialFormData(null);
              setHasUnsavedChanges(false);
              resetForm();
            }}>
              CANCELAR
            </Button>
          )}
        </div>
      </form>

      <div className={styles.tableContainer}>
        <h3 className={styles.tableTitle}>Líneas de Presupuesto</h3>
        <Table
          headers={budgetLinesTableHeaders}
          data={budgetLinesTableData}
          onPress={handleBudgetLineClick}
          textColorConditions={textColorConditions}
        />
      </div>

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
    </div>
  );
};

export default BudgetLines;
